import { useEffect, type CSSProperties, type ReactNode } from "react";
import { Link, usePath } from "./lib/router.tsx";
import { useProfile } from "./lib/profile.ts";
import { useTheme } from "./lib/theme.ts";
import { PropPayout } from "./calculators/PropPayout.tsx";
import { Compare } from "./calculators/Compare.tsx";
import { Quarterly } from "./calculators/Quarterly.tsx";
import { KeyFigures } from "./components/KeyFigures.tsx";
import { DueDates } from "./components/DueDates.tsx";
import { HeroDemo } from "./components/HeroDemo.tsx";
import { Logo } from "./components/Logo.tsx";
import { PayoutStory } from "./components/PayoutStory.tsx";
import { Banner } from "./components/Banner.tsx";
import { ReportPitch } from "./components/ReportPitch.tsx";
import { FolderNav, type FolderSection } from "./components/FolderNav.tsx";
import { useReveal } from "./lib/scroll.ts";
import { Organizer, REPORT_STEPS } from "./organizer/Organizer.tsx";
import { GUIDES, GUIDES_PATH, GuidesIndex, OtherGuides } from "./guides/Guides.tsx";
import { CONTACT_PATH, Contact } from "./Contact.tsx";

const ORGANIZER_PATH = "/organizer";

interface Page {
  path: string;
  nav: string;
  title: string;
  h1: string;
  lede: string;
  blurb: string;
  render: (p: ReturnType<typeof useProfile>) => ReactNode;
}

export const PAGES: Page[] = [
  {
    path: "/prop-firm-payout-tax-calculator",
    nav: "Prop payout tax",
    title: "Prop Firm Payout Tax Calculator (2026)",
    h1: "How much tax will I owe on my prop payouts?",
    lede: "Payouts from Topstep, Apex, Tradeify and other prop firms usually arrive on a 1099-NEC as self-employment income, with nothing withheld. See what to set aside, with every step of the math shown.",
    blurb: "What to set aside from every payout, after fees.",
    render: ([profile, update]) => <PropPayout profile={profile} update={update} />,
  },
  {
    path: "/futures-60-40-tax-calculator",
    nav: "60/40 calculator",
    title: "Futures 60/40 Tax Calculator (2026)",
    h1: "How much does the 60/40 rule save me?",
    lede: "Regulated futures, like those on CME exchanges, are Section 1256 contracts: 60% of the gain is taxed as long-term and 40% as short-term, however long you held each trade. Compare it with ordinary income.",
    blurb: "Your futures gain under 60/40 versus ordinary rates.",
    render: ([profile, update]) => <Compare mode="6040" profile={profile} update={update} />,
  },
  {
    path: "/prop-firm-vs-personal-account-taxes",
    nav: "Prop vs personal account",
    title: "Prop Firm vs Personal Futures Account Taxes (2026)",
    h1: "Prop firm or my personal account: which costs less in tax?",
    lede: "The same profit is taxed very differently as prop payouts (Schedule C plus self-employment tax) than as futures gains in your personal account (60/40).",
    blurb: "The same profit, taxed two different ways.",
    render: ([profile, update]) => <Compare mode="prop" profile={profile} update={update} />,
  },
  {
    path: "/quarterly-estimated-tax-calculator",
    nav: "Quarterly estimates",
    title: "Quarterly Estimated Tax Calculator for Traders (2026)",
    h1: "What should my next quarterly payment be?",
    lede: "Prop firms don't withhold tax. Pay at least the IRS safe-harbor amount by each due date and you won't owe an underpayment penalty, even if your income jumps.",
    blurb: "Your safe-harbor amount and remaining due dates.",
    render: ([profile, update]) => <Quarterly profile={profile} update={update} />,
  },
];

/** The form each calculator works out, shown over its name in the top bar's folder. */
const PAGE_CODES = ["Schedule C", "Form 6781", "Sch. C + 6781", "Form 1040-ES"];

/** What a guide's folder page says under its name. */
const GUIDE_LINES: Record<string, string> = {
  turbotax: "Online and Desktop, step by step",
  "hr-block": "Which product handles Form 6781",
  freetaxusa: "Free federal return, step by step",
};

function navSections(path: string): FolderSection[] {
  return [
    {
      name: "Calculators",
      tone: "calc",
      meta: "4 free tools",
      pages: PAGES.map((p, i) => ({ href: p.path, code: PAGE_CODES[i]!, name: p.nav, line: p.blurb, current: p.path === path })),
    },
    {
      name: "Filing guides",
      tone: "guide",
      meta: "3 tax apps",
      pages: [
        { href: GUIDES_PATH, code: "Start here", name: "All guides", line: "Pick the tax app you use.", current: path === GUIDES_PATH },
        ...GUIDES.map((g) => ({ href: g.path, code: "Guide", name: g.name, line: GUIDE_LINES[g.slug] ?? "", current: g.path === path })),
      ],
    },
    {
      name: "Tax report",
      tone: "report",
      meta: "Full year, 5 steps",
      pages: REPORT_STEPS.map((s, i) => ({ href: `${ORGANIZER_PATH}#${s.id}`, code: `Step ${i + 1}`, name: s.title, line: s.blurb, current: false })),
    },
  ];
}

const Icon = {
  moon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
    </svg>
  ),
  sun: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ),
  tick: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  ),
  steps: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  ),
  check: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  lock: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  ),
};

function Home() {
  const root = useReveal<HTMLDivElement>();
  return (
    <div ref={root}>
      <section className="hero">
        <div className="shell">
          <div className="hero-inner">
            <div>
              <h1>Taxes for futures and prop firm traders.</h1>
              <p className="lede">
                See how much tax you'll owe on your trading. It's free and there's nothing to sign up for. Get a report you can copy straight
                into TurboTax, H&R Block or FreeTaxUSA.
              </p>
              <div className="hero-cta">
                <Link href={PAGES[0]!.path} className="btn btn--primary">
                  Try it with my numbers
                </Link>
                <Link href={ORGANIZER_PATH} className="btn btn--ghost">
                  Start my 2026 report
                </Link>
              </div>
              <ul className="hero-assure">
                <li>{Icon.tick}Free, no sign-up</li>
                <li>{Icon.tick}Runs in your browser</li>
                <li>{Icon.tick}Final 2026 IRS figures</li>
              </ul>
            </div>
            <HeroDemo>
              <KeyFigures links={{ prop: PAGES[0]!.path, sixtyForty: PAGES[1]!.path, quarterly: PAGES[3]!.path, guides: GUIDES_PATH }} />
            </HeroDemo>
          </div>
        </div>
      </section>

      <DueDates href={PAGES[3]!.path} />

      <ReportPitch href={ORGANIZER_PATH} taxYear={2026} />

      <PayoutStory calculatorHref={PAGES[0]!.path} />

      <Banner />

      <section className="band band--quiet">
        <div className="shell">
          <h2>Try a free calculator</h2>
          <p className="band-lede">Quick answers for one part of your year, on the same tested tax engine as the report.</p>
          <div className="tools tools--compact">
            {PAGES.map((p, i) => (
              <Link key={p.path} href={p.path} className="tool" data-reveal style={{ "--i": i } as CSSProperties}>
                <span className="tool-name">{p.nav}</span>
                <span className="tool-body">{p.blurb}</span>
                <span className="tool-go">Open →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="band">
        <div className="shell">
          <div className="trust">
            <div data-reveal>
              {Icon.steps}
              <h3>Shows its work</h3>
              <p>Open "Why this amount?" on any result to see each step, with the IRS rule it came from.</p>
            </div>
            <div data-reveal style={{ "--i": 1 } as CSSProperties}>
              {Icon.check}
              <h3>Final 2026 figures</h3>
              <p>Brackets, wage base and safe-harbor rules from Rev. Proc. 2025-32 and the final 2026 Form 1040-ES, tested against IRS worked examples.</p>
            </div>
            <div data-reveal style={{ "--i": 2 } as CSSProperties}>
              {Icon.lock}
              <h3>Stays on your device</h3>
              <p>The math runs in your browser. Nothing you type is sent anywhere, and there's no account to make.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function App() {
  const path = usePath();
  const profile = useProfile();
  const [theme, toggleTheme] = useTheme();
  const page = PAGES.find((p) => p.path === path);
  const guide = GUIDES.find((g) => g.path === path);

  useEffect(() => {
    document.title = page
      ? `${page.title} | Futures Tax Report`
      : guide
        ? `${guide.title} | Futures Tax Report`
        : path === GUIDES_PATH
          ? "How to Enter Futures and Prop Firm Income in Tax Software (2026) | Futures Tax Report"
          : path === ORGANIZER_PATH
        ? "2026 Tax Report for Futures and Prop Traders | Futures Tax Report"
        : path === CONTACT_PATH
          ? "Contact | Futures Tax Report"
          : "Futures Tax Report: tax tools for futures and prop-firm traders";
  }, [page, guide, path]);

  return (
    <>
      <header className="top">
        <FolderNav sections={navSections(path)} path={path}>
          <Link href="/" className="brand">
            <Logo outline />
            <span className="brand-name">Futures Tax Report</span>
          </Link>
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
          >
            {theme === "dark" ? Icon.sun : Icon.moon}
          </button>
        </FolderNav>
      </header>
      <main>
        {page ? (
          <div className="shell">
            <section className="page-head">
              <p className="crumbs">
                <Link href="/">Free calculators</Link> / {page.nav}
              </p>
              <h1>{page.h1}</h1>
              <p className="lede">{page.lede}</p>
            </section>
            {page.render(profile)}
          </div>
        ) : guide ? (
          <div className="shell guide">
            <section className="page-head">
              <p className="crumbs">
                <Link href="/">Home</Link> / <Link href={GUIDES_PATH}>Filing guides</Link> / {guide.name}
              </p>
              <h1>{guide.h1}</h1>
              <p className="lede">{guide.lede}</p>
            </section>
            {guide.render()}
            <OtherGuides current={guide.slug} />
          </div>
        ) : path === GUIDES_PATH ? (
          <div className="shell guide">
            <section className="page-head">
              <p className="crumbs">
                <Link href="/">Home</Link> / Filing guides
              </p>
              <h1>Entering your report in tax software</h1>
              <p className="lede">
                Step-by-step for TurboTax, H&R Block and FreeTaxUSA: which edition handles Form 6781 and Schedule C, where each figure goes, and the
                mistakes that double-count income. Each step says how well we could confirm it.
              </p>
            </section>
            <GuidesIndex />
          </div>
        ) : path === ORGANIZER_PATH ? (
          <div className="shell">
            <section className="page-head no-print">
              <p className="crumbs">
                <Link href="/">Home</Link> / Tax report
              </p>
              <h1>Your 2026 tax report</h1>
              <p className="lede">
                Add your futures accounts, prop firms and 1099s. Each payout counts once, corrected forms replace originals only when you confirm, and
                nothing is filled in for you. Everything stays in this browser.
              </p>
            </section>
            <Organizer />
          </div>
        ) : path === CONTACT_PATH ? (
          <Contact />
        ) : path === "/" ? (
          <Home />
        ) : (
          <div className="shell">
            <section className="page-head">
              <h1>Page not found</h1>
              <p className="lede">
                <Link href="/">Go to the calculators</Link>
              </p>
            </section>
          </div>
        )}
      </main>
      <footer className="foot">
        <div className="shell">
          <p>
            Estimates for the 2026 tax year, federal only, using Rev. Proc. 2025-32, the final 2026 Form 1040-ES and the 2026 Social Security wage
            base. Educational information, not tax advice. Your answers stay in this browser.
          </p>
          <Link href={CONTACT_PATH} className="foot-link">
            Contact
          </Link>
        </div>
      </footer>
    </>
  );
}
